import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { STORAGE } from "@/_config";
import { incrementLoader, decrementLoader } from '@/context/GlobalLoaderContext';

// ---------------------------------------------------------------------------
// Token refresh helpers (no React dependency — reads/writes localStorage directly)
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1/";

let isRefreshing = false;
type RefreshSubscriber = { resolve: (token: string) => void; reject: (err: Error) => void };
let refreshSubscribers: RefreshSubscriber[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((s) => s.resolve(token));
  refreshSubscribers = [];
}

function onRefreshFailed(err: Error) {
  refreshSubscribers.forEach((s) => s.reject(err));
  refreshSubscribers = [];
}

function forceLogout() {
  localStorage.removeItem(STORAGE);
  window.dispatchEvent(new CustomEvent("auth:force-logout"));
}

// Returns the new access token on success.
// Returns null if the refresh token is definitively expired/invalid (401/403) — caller should logout.
// Throws for transient errors (network, 5xx) — caller should NOT logout.
async function doTokenRefresh(): Promise<string | null> {
  const raw = localStorage.getItem(STORAGE);
  if (!raw) return null;
  const stored = JSON.parse(raw);
  const refreshToken = stored.refreshToken;
  if (!refreshToken) return null;

  try {
    const res = await axios.post(
      `${API_BASE}auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { "Content-Type": "application/json", Accept: "application/json" } },
    );

    // Handle both envelope shapes: { data: { access_token } } and { access_token }
    const payload = res.data?.data ?? res.data;
    const newAccessToken: string | undefined = payload?.access_token;
    const newRefreshToken: string | undefined = payload?.refresh_token ?? refreshToken;
    if (!newAccessToken) return null; // unexpected response shape — treat as auth failure

    // Rotate stored tokens in localStorage
    stored.token = newAccessToken;
    stored.refreshToken = newRefreshToken;
    localStorage.setItem(STORAGE, JSON.stringify(stored));

    // Notify AuthContext so React state stays in sync (prevents stale-token overwrite)
    window.dispatchEvent(new CustomEvent("auth:token-refreshed", {
      detail: { token: newAccessToken, refreshToken: newRefreshToken },
    }));

    return newAccessToken;
  } catch (err: unknown) {
    const status: number | undefined = (err as any)?.response?.status;

    // 401/403/422 → refresh token is genuinely expired or invalid → must logout
    if (status === 401 || status === 403 || status === 422) return null;

    // No response at all (network error, timeout) or 5xx server error:
    // This is NOT an auth failure. Throw so the caller can skip the logout.
    throw err;
  }
}

// ---------------------------------------------------------------------------
// LeadNova API response envelope
// ---------------------------------------------------------------------------

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
  meta: {
    pagination?: {
      current_page: number;
      per_page: number;
      total: number;
      next_cursor?: string | null;
      prev_cursor?: string | null;
    };
  } | null;
}

// ---------------------------------------------------------------------------
// Stored token shape (matches AuthContext)
// ---------------------------------------------------------------------------

interface StoredUser {
  token?: string;
  access_token?: string;
}

const getToken = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE);
    if (!raw) return null;
    const user = JSON.parse(raw) as StoredUser;
    return user.access_token ?? user.token ?? null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:8002/api/v1/",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    timeout: 30_000,
  });

  // Attach Bearer token and idempotency key on every request
  instance.interceptors.request.use((config) => {
    incrementLoader();
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Generate a correlation ID for tracing
    config.headers["X-Correlation-ID"] = `fe-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Auto-generate idempotency key for all mutating requests.
    // The API middleware requires this header on POST routes.
    const method = (config.method ?? "").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const key =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      config.headers["Idempotency-Key"] = key;
    }

    return config;
  });

  // Unwrap the response envelope — throw on success: false
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      const envelope = response.data;

      if (envelope && typeof envelope === "object" && "success" in envelope) {
        if (!envelope.success) {
          decrementLoader();
          const error = new Error(envelope.error?.message ?? "API request failed");
          (error as Error & { apiError: typeof envelope.error; meta: typeof envelope.meta }).apiError = envelope.error;
          (error as Error & { apiError: typeof envelope.error; meta: typeof envelope.meta }).meta = envelope.meta;
          throw error;
        }
      }

      decrementLoader();
      return response;
    },
    async (error) => {
      decrementLoader();
      if (error.response?.status === 401) {
        const originalRequest = error.config;

        // Avoid infinite loop on the refresh endpoint itself
        if (originalRequest?.url?.includes("auth/refresh")) {
          forceLogout();
          return Promise.reject(error);
        }

        // For login/register endpoints, 401 = wrong credentials.
        // Don't try to refresh tokens or redirect — let the error bubble up
        // so the page can display a proper message without reloading.
        const isAuthEndpoint =
          originalRequest?.url?.includes("auth/login") ||
          originalRequest?.url?.includes("auth/register") ||
          originalRequest?.url?.includes("auth/logout");

        if (!isAuthEndpoint && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            // Queue this request until the ongoing refresh resolves or fails
            return new Promise((resolve, reject) => {
              refreshSubscribers.push({
                resolve: (token) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(instance(originalRequest));
                },
                reject,
              });
            });
          }

          isRefreshing = true;
          let newToken: string | null = null;
          let refreshThrew = false;

          try {
            newToken = await doTokenRefresh();
          } catch {
            // Transient error (network, 5xx) — do NOT logout
            refreshThrew = true;
          } finally {
            isRefreshing = false;
          }

          if (newToken) {
            onRefreshed(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          }

          if (refreshThrew) {
            // Network/server error during refresh — reject all queued requests
            // but keep the user logged in (tokens may still be valid)
            const networkErr = new Error("Network error. Please check your connection and try again.");
            onRefreshFailed(networkErr);
            return Promise.reject(networkErr);
          }

          // Refresh token is expired/invalid (doTokenRefresh returned null) — force logout
          onRefreshFailed(new Error("Session expired. Please log in again."));
          forceLogout();
          return Promise.reject(error);
        }
      }

      // 402 — plan expired or inactive; redirect to upgrade page
      if (error.response?.status === 402) {
        const code = (error.response?.data as any)?.error?.code ?? '';
        if (code === 'TRIAL_EXPIRED' || code === 'PLAN_INACTIVE') {
          window.dispatchEvent(new CustomEvent('auth:plan-expired', { detail: { code } }));
        }
        return Promise.reject(error);
      }

      // 422 with an auth error code means the backend rejected the session — force logout.
      if (error.response?.status === 422) {
        const code = (error.response?.data as any)?.error?.code ?? '';
        const authCodes = ['TOKEN_EXPIRED', 'UNAUTHENTICATED', 'INVALID_TOKEN', 'SESSION_EXPIRED'];
        const isAuthEndpoint =
          error.config?.url?.includes("auth/login") ||
          error.config?.url?.includes("auth/register");
        if (authCodes.includes(code) && !isAuthEndpoint) {
          forceLogout();
          return Promise.reject(error);
        }
      }

      // Extract a user-friendly message from the API envelope
      const envelope = error.response?.data;
      if (envelope && typeof envelope === "object" && "error" in envelope && envelope.error) {
        const apiErr = envelope.error as { code: string; message: string; details?: Record<string, string[]> };
        let message = apiErr.message ?? "An error occurred.";

        // For validation errors, surface the first field-level message
        if (apiErr.details && typeof apiErr.details === "object") {
          const first = Object.values(apiErr.details as Record<string, string[]>).flat()[0];
          if (first) message = first;
        }

        const friendlyError = new Error(message);
        (friendlyError as any).apiError = apiErr;
        (friendlyError as any).statusCode = error.response?.status;
        return Promise.reject(friendlyError);
      }

      // Generic message for 5xx errors without an envelope
      if ((error.response?.status ?? 0) >= 500) {
        return Promise.reject(new Error("Server error. Please try again later."));
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export const apiClient = createApiClient();

// ---------------------------------------------------------------------------
// Typed helper methods — use these in TanStack Query queryFn / mutationFn
// ---------------------------------------------------------------------------

export const get = async <T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> => {
  const response = await apiClient.get<ApiResponse<T>>(url, { params, ...config });
  return response.data;
};

export const post = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data;
};

export const put = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data;
};

export const patch = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data;
};

export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data;
};
